import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCelebration } from '@/context/CelebrationContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import BackButton from '@/components/BackButton';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Users, Check, ArrowRight, Plus, Lock, Unlock, Sparkles, Star, Trash2, TrendingUp, Clock, SortAsc, Search, X, Lightbulb } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Keyword mappings for smart search recommendations
const KEYWORD_MAPPINGS = {
  // Relationships & Family
  divorce: ['relationship', 'boundaries', 'single', 'co-parenting', 'healing', 'transition'],
  marriage: ['relationship', 'boundaries', 'couples', 'communication', 'intimacy'],
  relationship: ['boundaries', 'dating', 'love', 'partnership', 'discernment'],
  dating: ['relationship', 'boundaries', 'single', 'discernment'],
  single: ['dating', 'self-love', 'independence', 'boundaries'],
  mom: ['parenting', 'motherhood', 'family', 'balance', 'self-care'],
  mother: ['parenting', 'motherhood', 'family', 'balance', 'self-care'],
  parenting: ['mom', 'family', 'children', 'balance'],
  children: ['parenting', 'family', 'mom', 'balance'],
  family: ['parenting', 'boundaries', 'relationship', 'healing'],
  
  // Career & Business
  career: ['job', 'leadership', 'business', 'professional', 'resume', 'linkedin'],
  job: ['career', 'resume', 'linkedin', 'interview', 'opportunities'],
  business: ['entrepreneur', 'career', 'leadership', 'networking'],
  entrepreneur: ['business', 'startup', 'leadership', 'networking'],
  leadership: ['career', 'influence', 'management', 'professional'],
  resume: ['job', 'career', 'linkedin', 'interview'],
  linkedin: ['resume', 'job', 'career', 'networking', 'professional'],
  networking: ['business', 'career', 'professional', 'connections'],
  
  // Wellness & Health
  health: ['wellness', 'fitness', 'self-care', 'mental', 'healing'],
  wellness: ['health', 'self-care', 'balance', 'mental', 'fitness'],
  mental: ['health', 'wellness', 'therapy', 'healing', 'anxiety', 'depression'],
  anxiety: ['mental', 'wellness', 'stress', 'healing', 'support'],
  depression: ['mental', 'wellness', 'healing', 'support', 'therapy'],
  stress: ['wellness', 'balance', 'self-care', 'mental', 'burnout'],
  burnout: ['stress', 'career', 'balance', 'self-care', 'energy'],
  fitness: ['health', 'wellness', 'exercise', 'body', 'energy'],
  weight: ['health', 'fitness', 'wellness', 'body', 'self-love'],
  
  // Spiritual & Personal Growth
  faith: ['prayer', 'spiritual', 'encouragement', 'purpose'],
  prayer: ['faith', 'spiritual', 'encouragement', 'support'],
  spiritual: ['faith', 'prayer', 'purpose', 'growth', 'meditation'],
  healing: ['trauma', 'recovery', 'wellness', 'support', 'growth'],
  trauma: ['healing', 'recovery', 'support', 'therapy', 'mental'],
  growth: ['personal', 'development', 'purpose', 'goals', 'transformation'],
  purpose: ['growth', 'career', 'spiritual', 'goals', 'meaning'],
  
  // Life Transitions
  transition: ['change', 'new', 'starting', 'divorce', 'career'],
  grief: ['loss', 'healing', 'support', 'encouragement'],
  loss: ['grief', 'healing', 'support', 'encouragement'],
  retirement: ['transition', 'new', 'purpose', 'wellness'],
  empty: ['transition', 'parenting', 'purpose', 'new'],
  
  // Goals & Accountability
  goals: ['accountability', 'wins', 'growth', 'purpose'],
  accountability: ['goals', 'wins', 'support', 'growth'],
  wins: ['goals', 'accountability', 'celebration', 'encouragement'],
  
  // Specific Topics
  boundaries: ['relationship', 'self-care', 'discernment', 'family'],
  money: ['finance', 'budget', 'career', 'business'],
  finance: ['money', 'budget', 'investing', 'career'],
  education: ['learning', 'career', 'growth', 'development']
};

// Get related keywords for a search term
const getRelatedKeywords = (searchTerm) => {
  const term = searchTerm.toLowerCase().trim();
  const related = new Set();
  
  // Direct match
  if (KEYWORD_MAPPINGS[term]) {
    KEYWORD_MAPPINGS[term].forEach(k => related.add(k));
  }
  
  // Partial matches
  Object.entries(KEYWORD_MAPPINGS).forEach(([key, values]) => {
    if (key.includes(term) || term.includes(key)) {
      related.add(key);
      values.forEach(v => related.add(v));
    }
    if (values.some(v => v.includes(term) || term.includes(v))) {
      related.add(key);
      values.forEach(v => related.add(v));
    }
  });
  
  return Array.from(related);
};

const CommunitiesPage = () => {
  const { user } = useAuth();
  const { celebrate, grandCelebrate } = useCelebration();
  const [communities, setCommunities] = useState([]);
  const [userCircles, setUserCircles] = useState([]);
  const [recommendedCircles, setRecommendedCircles] = useState([]);
  const [userCommunities, setUserCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joiningId, setJoiningId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('recommended');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCircle, setNewCircle] = useState({
    name: '',
    description: '',
    is_open: true,
    focus_areas: []
  });

  const categories = ['all', 'Core Community', 'Sister Circles', 'Program'];
  const focusAreaOptions = ['boundaries', 'influence', 'energy', 'purpose'];
  
  const sortOptions = [
    { value: 'recommended', label: 'Recommended', icon: Sparkles },
    { value: 'popular', label: 'Most Popular', icon: TrendingUp },
    { value: 'newest', label: 'Newest First', icon: Clock },
    { value: 'alphabetical', label: 'A-Z', icon: SortAsc }
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchCommunities(),
        fetchUserCommunities(),
        fetchUserCircles(),
        fetchRecommendedCircles()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommunities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/communities`);
      setCommunities(response.data);
    } catch (error) {
      console.error('Failed to fetch communities:', error);
    }
  };

  const fetchUserCommunities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/communities`);
      setUserCommunities(response.data.map(c => c.id));
    } catch (error) {
      console.error('Failed to fetch user communities:', error);
    }
  };

  const fetchUserCircles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/circles/user-created`);
      setUserCircles(response.data);
    } catch (error) {
      console.error('Failed to fetch user circles:', error);
    }
  };

  const fetchRecommendedCircles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/circles/recommended`);
      setRecommendedCircles(response.data);
    } catch (error) {
      console.error('Failed to fetch recommended circles:', error);
    }
  };

  const handleJoinLeave = async (communityId, isMember, isUserCircle = false) => {
    setJoiningId(communityId);
    try {
      const endpoint = isUserCircle ? 'circles' : 'communities';
      if (isMember) {
        await axios.post(`${API_URL}/api/${endpoint}/${communityId}/leave`);
        if (isUserCircle) {
          setUserCircles(prev => prev.map(c => c.id === communityId ? {...c, is_member: false} : c));
        } else {
          setUserCommunities(prev => prev.filter(id => id !== communityId));
        }
        toast.success('Left the circle');
      } else {
        await axios.post(`${API_URL}/api/${endpoint}/${communityId}/join`);
        if (isUserCircle) {
          setUserCircles(prev => prev.map(c => c.id === communityId ? {...c, is_member: true} : c));
        } else {
          setUserCommunities(prev => [...prev, communityId]);
        }
        celebrate('hearts', 'Welcome to the circle, Sis!');
      }
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setJoiningId(null);
    }
  };

  const handleCreateCircle = async () => {
    if (!newCircle.name.trim() || !newCircle.description.trim()) {
      toast.error('Please fill in the name and description');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/circles/create`, newCircle);
      grandCelebrate('confetti', 'milestone');
      setShowCreateDialog(false);
      setNewCircle({ name: '', description: '', is_open: true, focus_areas: [] });
      fetchUserCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create circle');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCircle = async (circleId) => {
    if (!confirm('Are you sure you want to delete this circle?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/circles/${circleId}`);
      toast.success('Circle deleted');
      fetchUserCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete circle');
    }
  };

  // Get related keywords for current search
  const relatedKeywords = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    return getRelatedKeywords(searchQuery);
  }, [searchQuery]);

  // Check if a community/circle matches the search
  const matchesSearch = (item) => {
    if (!searchQuery || searchQuery.length < 2) return true;
    
    const query = searchQuery.toLowerCase();
    const name = (item.name || '').toLowerCase();
    const description = (item.description || '').toLowerCase();
    const category = (item.category || '').toLowerCase();
    const focusAreas = (item.focus_areas || []).map(f => f.toLowerCase());
    
    // Direct text match
    if (name.includes(query) || description.includes(query) || category.includes(query)) {
      return true;
    }
    
    // Focus area match
    if (focusAreas.some(f => f.includes(query))) {
      return true;
    }
    
    // Related keyword match
    const related = getRelatedKeywords(query);
    if (related.some(keyword => 
      name.includes(keyword) || 
      description.includes(keyword) ||
      focusAreas.some(f => f.includes(keyword))
    )) {
      return true;
    }
    
    return false;
  };

  // Filter communities based on category AND search
  const filteredCommunities = useMemo(() => {
    let filtered = selectedCategory === 'all' 
      ? communities 
      : selectedCategory === 'Sister Circles'
      ? []
      : communities.filter(c => c.category === selectedCategory);
    
    // Apply search filter
    if (searchQuery && searchQuery.length >= 2) {
      filtered = filtered.filter(matchesSearch);
    }
    
    return filtered;
  }, [communities, selectedCategory, searchQuery]);

  // Filter user circles based on search
  const filteredUserCircles = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return userCircles;
    return userCircles.filter(matchesSearch);
  }, [userCircles, searchQuery]);

  // Sort communities based on selected option - Core Community always first
  const sortedCommunities = [...filteredCommunities].sort((a, b) => {
    // Core Community always comes first
    if (a.category === 'Core Community' && b.category !== 'Core Community') return -1;
    if (b.category === 'Core Community' && a.category !== 'Core Community') return 1;
    
    switch (sortBy) {
      case 'popular':
        return (b.member_count || 0) - (a.member_count || 0);
      case 'newest':
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case 'alphabetical':
        return (a.name || '').localeCompare(b.name || '');
      case 'recommended':
      default:
        // Recommended circles first, then by member count
        const aIsRecommended = recommendedCircles.some(r => r.id === a.id);
        const bIsRecommended = recommendedCircles.some(r => r.id === b.id);
        if (aIsRecommended && !bIsRecommended) return -1;
        if (!aIsRecommended && bIsRecommended) return 1;
        return (b.member_count || 0) - (a.member_count || 0);
    }
  });

  // Sort user circles too (using filtered list)
  const sortedUserCircles = [...filteredUserCircles].sort((a, b) => {
    switch (sortBy) {
      case 'popular':
        return (b.member_count || 0) - (a.member_count || 0);
      case 'newest':
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case 'alphabetical':
        return (a.name || '').localeCompare(b.name || '');
      case 'recommended':
      default:
        // Featured circles first, then by member count
        if (a.is_featured && !b.is_featured) return -1;
        if (!a.is_featured && b.is_featured) return 1;
        return (b.member_count || 0) - (a.member_count || 0);
    }
  });

  // Check if a community is recommended
  const isRecommended = (communityId) => recommendedCircles.some(r => r.id === communityId);

  // Show user circles when "Sister Circles" or "all" is selected
  const showUserCircles = selectedCategory === 'all' || selectedCategory === 'Sister Circles';

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="communities-page">
        <BackButton to="/dashboard" label="Back to Dashboard" />
        
        {/* Banner */}
        <div className="mb-8 rounded-2xl overflow-hidden shadow-lg relative h-40 md:h-48" style={{background: 'linear-gradient(135deg, #2D4739 0%, #2D4739 60%, #C4704B 100%)'}}>
          <div className="absolute inset-0 flex items-center justify-between px-6 md:px-10">
            <div>
              <p className="text-white text-sm md:text-base font-medium opacity-90">Find Your People</p>
              <p className="text-white text-xl md:text-2xl font-heading font-bold">Safe Spaces to Speak What's Sacred</p>
              <p className="text-white text-sm mt-1 opacity-80">Sister Circles for every season</p>
            </div>
            <Users className="h-16 w-16 md:h-20 md:w-20 text-white opacity-20" />
          </div>
        </div>
        
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold text-forest">
              Sister Circles
            </h1>
            <p className="text-gray-600 mt-2">
              Find your people. Safe spaces to speak what&apos;s sacred.
            </p>
          </div>
          
          {/* Create Circle Button */}
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-circle-btn">
                <Plus className="h-4 w-4 mr-2" /> Create a Circle
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-forest">Create Your Circle</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="circle-name">Circle Name</Label>
                  <Input
                    id="circle-name"
                    value={newCircle.name}
                    onChange={(e) => setNewCircle({...newCircle, name: e.target.value})}
                    placeholder="e.g., Atlanta Sister Diamonds"
                    className="input-base"
                  />
                </div>
                <div>
                  <Label htmlFor="circle-description">Description</Label>
                  <Textarea
                    id="circle-description"
                    value={newCircle.description}
                    onChange={(e) => setNewCircle({...newCircle, description: e.target.value})}
                    placeholder="What is this circle about? Who should join?"
                    className="input-base min-h-24"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Open Circle</Label>
                    <p className="text-sm text-gray-500">Anyone can join</p>
                  </div>
                  <Switch
                    checked={newCircle.is_open}
                    onCheckedChange={(checked) => setNewCircle({...newCircle, is_open: checked})}
                  />
                </div>
                <div>
                  <Label>Focus Areas (optional)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {focusAreaOptions.map(area => (
                      <Badge
                        key={area}
                        variant={newCircle.focus_areas.includes(area) ? 'default' : 'outline'}
                        className={`cursor-pointer ${newCircle.focus_areas.includes(area) ? 'bg-terracotta' : ''}`}
                        onClick={() => {
                          const areas = newCircle.focus_areas.includes(area)
                            ? newCircle.focus_areas.filter(a => a !== area)
                            : [...newCircle.focus_areas, area];
                          setNewCircle({...newCircle, focus_areas: areas});
                        }}
                      >
                        {area.charAt(0).toUpperCase() + area.slice(1)}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                <Button className="btn-primary" onClick={handleCreateCircle} disabled={creating}>
                  {creating ? 'Creating...' : 'Create Circle'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search and Category Filters */}
        <div className="space-y-4 mb-8">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search circles by topic (e.g., divorce, career, wellness, boundaries...)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-base pl-10 pr-10"
              data-testid="community-search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          
          {/* Related Keywords Suggestions */}
          {searchQuery && searchQuery.length >= 2 && relatedKeywords.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <Lightbulb className="h-4 w-4 text-terracotta" />
                <span>Related:</span>
              </div>
              {relatedKeywords.slice(0, 8).map((keyword) => (
                <Badge
                  key={keyword}
                  variant="outline"
                  className="cursor-pointer hover:bg-terracotta/10 hover:border-terracotta transition-colors"
                  onClick={() => setSearchQuery(keyword)}
                >
                  {keyword}
                </Badge>
              ))}
            </div>
          )}
          
          {/* Category Filters and Sort */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  className={selectedCategory === category ? 'btn-primary' : 'btn-outline'}
                  onClick={() => setSelectedCategory(category)}
                  data-testid={`filter-${category}`}
                >
                  {category === 'all' ? 'All Circles' : category}
                </Button>
              ))}
            </div>
            
            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Sort by:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px]" data-testid="sort-dropdown">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <option.icon className="h-4 w-4" />
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Search Results Info */}
        {searchQuery && searchQuery.length >= 2 && (
          <div className="mb-6 p-4 bg-sand/30 rounded-lg">
            <p className="text-forest">
              <span className="font-semibold">
                {sortedCommunities.length + sortedUserCircles.length} circles
              </span>
              {' '}found for "<span className="text-terracotta font-medium">{searchQuery}</span>"
              {relatedKeywords.length > 0 && (
                <span className="text-gray-500"> (including related topics)</span>
              )}
            </p>
          </div>
        )}

        {/* Recommended Circles Section - Only show when not searching */}
        {recommendedCircles.length > 0 && selectedCategory === 'all' && !searchQuery && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-terracotta" />
              <h2 className="font-heading text-xl font-semibold text-forest">Recommended for You</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recommendedCircles.slice(0, 3).map((circle) => (
                <Card key={circle.id} className="card-base border-terracotta/30 bg-terracotta/5">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-heading font-semibold text-forest">{circle.name}</h3>
                        <p className="text-sm text-gray-600 line-clamp-2">{circle.description}</p>
                      </div>
                      <Badge variant="outline" className="flex items-center gap-1">
                        {circle.is_open ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                        {circle.is_open ? 'Open' : 'Closed'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-sm text-gray-500">{circle.member_count} sisters</span>
                      <Button
                        size="sm"
                        className={circle.is_member ? 'btn-outline' : 'btn-primary'}
                        onClick={() => handleJoinLeave(circle.id, circle.is_member, true)}
                        disabled={joiningId === circle.id || (!circle.is_open && !circle.is_member)}
                      >
                        {circle.is_member ? 'Joined' : 'Join'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Official Communities Grid - Core Communities FIRST */}
        {sortedCommunities.length > 0 && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <Star className="h-5 w-5 text-terracotta" />
              <h2 className="font-heading text-xl font-semibold text-forest">Core Communities</h2>
            </div>
            
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="card-base animate-pulse">
                    <div className="h-40 bg-sand rounded-lg mb-4" />
                    <div className="h-6 bg-sand rounded w-3/4 mb-2" />
                    <div className="h-4 bg-sand rounded w-full" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {sortedCommunities.map((community) => {
                  const isMember = userCommunities.includes(community.id);
                  const isRecommendedCommunity = isRecommended(community.id);
                  
                  return (
                    <Card 
                      key={community.id} 
                      className={`card-base overflow-hidden ${isRecommendedCommunity ? 'ring-2 ring-terracotta/50' : ''}`} 
                      data-testid={`community-card-${community.id}`}
                    >
                      <div className="relative h-40">
                        <img 
                          src={community.image_url} 
                          alt={community.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-forest/80 to-transparent" />
                        <div className="absolute bottom-3 left-3 right-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-white/90 text-forest">{community.category}</Badge>
                            {isRecommendedCommunity && (
                              <Badge className="bg-terracotta text-white flex items-center gap-1">
                                <Sparkles className="h-3 w-3" />
                                For You
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-heading text-xl font-semibold text-white">{community.name}</h3>
                        </div>
                      </div>
                      
                      <CardContent className="p-4">
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{community.description}</p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-gray-500">
                            <Users className="h-4 w-4" />
                            <span className="text-sm">{community.member_count} sisters</span>
                          </div>
                          
                          <Button
                            size="sm"
                            variant={isMember ? 'outline' : 'default'}
                            className={isMember ? 'btn-outline' : 'btn-primary'}
                            onClick={() => handleJoinLeave(community.id, isMember)}
                            disabled={joiningId === community.id}
                            data-testid={`join-btn-${community.id}`}
                          >
                            {joiningId === community.id ? (
                              'Processing...'
                            ) : isMember ? (
                              <>
                                <Check className="h-4 w-4 mr-1" />
                                Joined
                              </>
                            ) : (
                              'Join'
                            )}
                          </Button>
                        </div>
                        
                        {isMember && (
                          <Link to={`/communities/${community.id}`}>
                            <Button variant="ghost" className="w-full mt-3 text-forest hover:text-terracotta">
                              Enter Circle <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* User-Created Circles Section - Sister Circles AFTER Core */}
        {showUserCircles && sortedUserCircles.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-forest" />
              <h2 className="font-heading text-xl font-semibold text-forest">Sister-Created Circles</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedUserCircles.map((circle) => {
                const isCreator = circle.created_by === user?.id;
                const isAdmin = user?.role === 'admin';
                
                return (
                  <Card key={circle.id} className="card-base overflow-hidden" data-testid={`circle-card-${circle.id}`}>
                    <div className="relative h-32 bg-gradient-to-br from-forest to-terracotta">
                      <div className="absolute inset-0 bg-black/20" />
                      <div className="absolute bottom-3 left-3 right-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`${circle.is_open ? 'bg-green-500' : 'bg-amber-500'} text-white text-xs`}>
                            {circle.is_open ? <><Unlock className="h-3 w-3 mr-1" /> Open</> : <><Lock className="h-3 w-3 mr-1" /> Closed</>}
                          </Badge>
                          {circle.is_featured && (
                            <Badge className="bg-purple-500 text-white text-xs">
                              <Star className="h-3 w-3 mr-1" /> Featured
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-white">{circle.name}</h3>
                      </div>
                    </div>
                    
                    <CardContent className="p-4">
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{circle.description}</p>
                      <p className="text-xs text-gray-400 mb-3">Created by {circle.created_by_name}</p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-gray-500">
                          <Users className="h-4 w-4" />
                          <span className="text-sm">{circle.member_count} sisters</span>
                        </div>
                        
                        <div className="flex gap-2">
                          {(isCreator || isAdmin) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteCircle(circle.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant={circle.is_member ? 'outline' : 'default'}
                            className={circle.is_member ? 'btn-outline' : 'btn-primary'}
                            onClick={() => handleJoinLeave(circle.id, circle.is_member, true)}
                            disabled={joiningId === circle.id || (!circle.is_open && !circle.is_member)}
                          >
                            {joiningId === circle.id ? '...' : circle.is_member ? <><Check className="h-4 w-4 mr-1" /> Joined</> : 'Join'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {sortedCommunities.length === 0 && sortedUserCircles.length === 0 && !loading && (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No circles found in this category.</p>
            <Button className="btn-primary" onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" /> Create the First One
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CommunitiesPage;
